import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { RotateCw } from 'lucide-react';
import { Button } from './primitives.tsx';

/**
 * A rede embaixo de cada tela.
 *
 * Sem ela, um erro de renderização — ou um pedaço da tela que não baixou
 * depois de um deploy (as telas sob demanda trocam de nome a cada versão) —
 * deixava a página inteira em branco, sem dizer nada. Aqui a pessoa vê o que
 * houve e tem o botão que resolve o caso mais comum: carregar de novo.
 */
export class ErrorBoundary extends Component<{ children: ReactNode; resetKey?: string }, { error: Error | null }> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Erro na tela:', error, info.componentStack);
  }

  override componentDidUpdate(previous: { resetKey?: string }) {
    // Trocar de rota limpa o erro: a outra tela pode estar boa.
    if (this.state.error && previous.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  override render() {
    if (!this.state.error) return this.props.children;
    const chunk = /dynamically imported module|Loading chunk|Failed to fetch/i.test(this.state.error.message);
    return (
      <div role="alert" className="flex min-h-[40vh] flex-col items-start justify-center gap-3 px-1">
        <p className="font-display text-lg font-semibold">
          {chunk ? 'O painel foi atualizado enquanto estava aberto.' : 'Esta tela encontrou um erro.'}
        </p>
        <p className="max-w-md text-sm text-[var(--color-ink-soft)]">
          {chunk
            ? 'Carregue de novo para receber a versão nova. Nada do que já foi salvo se perde.'
            : `Carregue de novo. Se repetir, avise com esta mensagem: ${this.state.error.message}`}
        </p>
        <Button variant="primary" onClick={() => window.location.reload()}>
          <RotateCw aria-hidden="true" />
          Carregar de novo
        </Button>
      </div>
    );
  }
}
